/**
 * Emergency Profile Service
 *
 * Handles API calls for emergency profile data, QR codes, and consent management
 */

import { api } from "./api";
import type { EmergencyProfileData } from "@/types/emergency";

// Re-exported so existing importers of this module keep working; the
// definition now lives with the other emergency payload types.
export type { EmergencyProfileData };

export interface CompletenessScore {
  score: number;
  totalFields: number;
  completedFields: number;
  missingCritical: string[];
  categories: {
    allergies: boolean;
    medications: boolean;
    conditions: boolean;
    emergencyContacts: boolean;
    demographics: boolean;
  };
}

export interface QRCodeData {
  token: string;
  qrCodeUrl: string;
  expiresAt: string;
  accessUrl: string;
}

export interface ConsentSettings {
  shareAllergies: boolean;
  shareMedications: boolean;
  shareConditions: boolean;
  shareDevices: boolean;
  shareEmergencyContacts: boolean;
  shareDemographics: boolean;
  consentGivenAt?: string;
}

export interface AccessLogEntry {
  id: string;
  accessType: string;
  responderName: string;
  responderOrganization?: string;
  responderEmail?: string;
  accessReason?: string;
  grantedAt: string;
  expiresAt?: string;
  revokedAt?: string;
  ipAddress?: string;
  geolocation?: unknown;
  accessedResources?: string[];
  consentDocumented?: boolean;
}

export interface ValidationWarning {
  field: string;
  severity: "critical" | "high" | "medium" | "low";
  message: string;
  recommendation?: string;
}

export interface ValidationResult {
  isValid: boolean;
  completenessScore: number;
  warnings: ValidationWarning[];
  lastValidated: string;
}

/** A device as the emergency record reports it. */
export interface MedicalDevice {
  id: string;
  deviceType: string;
  deviceName: string | null;
  manufacturer: string | null;
  model: string | null;
  implantDate: string | null;
  /** null when nobody has recorded whether it is MRI safe. */
  mriSafe: boolean | null;
  notes: string | null;
  status: string;
}

/** What the form sends. Only the type is required. */
export interface MedicalDeviceInput {
  deviceType: string;
  deviceName?: string;
  manufacturer?: string;
  model?: string;
  implantDate?: string;
  mriSafe?: boolean;
  notes?: string;
}

class EmergencyService {
  /**
   * Get emergency profile data
   */
  async getEmergencyProfile(): Promise<EmergencyProfileData> {
    const response = await api.get("/emergency/profile");
    return response.data.data;
  }

  /**
   * Medical devices - pacemakers, pumps, implants.
   *
   * Kept here rather than with health records because the emergency card is
   * the only place they are read: whether an unconscious patient has an
   * implant, and whether it is safe in an MRI scanner, changes what a clinician
   * may do to them.
   */
  async getDevices(): Promise<MedicalDevice[]> {
    const response = await api.get("/emergency/devices");
    return response.data.data ?? [];
  }

  async addDevice(device: MedicalDeviceInput): Promise<void> {
    await api.post("/emergency/devices", device);
  }

  async removeDevice(id: string): Promise<void> {
    await api.delete(`/emergency/devices/${id}`);
  }

  /**
   * Get completeness score
   */
  async getCompletenessScore(): Promise<CompletenessScore> {
    const response = await api.get("/emergency/profile/completeness");
    return response.data.data;
  }

  /**
   * Generate QR code for emergency access
   */
  async generateQRCode(
    duration: number,
    consent: ConsentSettings,
  ): Promise<QRCodeData> {
    const response = await api.post("/emergency/qr/generate", {
      durationMinutes: duration,
      consent,
    });
    return response.data.data;
  }

  /**
   * Get active QR code
   */
  async getActiveQRCode(): Promise<QRCodeData | null> {
    try {
      const response = await api.get("/emergency/qr/current");
      return response.data.data;
    } catch (error: any) {
      // No active QR code
      return null;
    }
  }

  /**
   * Revoke active QR code
   */
  async revokeQRCode(): Promise<void> {
    await api.delete("/emergency/qr/revoke");
  }

  /**
   * Save consent settings
   */
  async saveConsent(consent: ConsentSettings): Promise<void> {
    await api.post("/emergency/consent", consent);
  }

  /**
   * Get consent settings
   */
  async getConsent(): Promise<ConsentSettings> {
    const response = await api.get("/emergency/consent");
    return response.data.data;
  }

  /**
   * Get access audit logs
   */
  async getAccessLogs(limit = 50): Promise<AccessLogEntry[]> {
    const response = await api.get(`/emergency/audit?limit=${limit}`);
    return response.data.data;
  }

  /**
   * Get validation result with warnings
   */
  async getValidation(): Promise<ValidationResult> {
    const response = await api.get("/emergency/profile/completeness");
    return response.data.data;
  }

  /**
   * Trigger manual profile validation
   */
  async triggerValidation(): Promise<ValidationResult> {
    const response = await api.post("/emergency/profile/validate");
    return response.data.data;
  }
}

export const emergencyService = new EmergencyService();
